import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, ArrowLeft, BookOpen, Users, Truck, ChevronRight,
  FileText, Settings, LogOut, HelpCircle, Clock, CheckCircle,
  AlertTriangle, Package, RefreshCw, Wrench, XCircle, Star
} from 'lucide-react';

interface HelpArticle {
  id: string;
  title: string;
  content: string;
  category: 'cliente' | 'fornecedor' | 'geral';
  tags: string[];
  lastUpdated: string;
  priority: 'high' | 'medium' | 'low';
}

const AjudaPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [userType, setUserType] = useState<'cliente' | 'fornecedor' | null>(null);

  useEffect(() => {
    // Detectar tipo de usuário do localStorage
    const storedUserType = localStorage.getItem('userType');
    if (storedUserType === 'cliente' || storedUserType === 'fornecedor') {
      setUserType(storedUserType);
      setSelectedCategory(storedUserType); // Mostrar automaticamente artigos do usuário
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    navigate('/');
  };

  const helpArticles: HelpArticle[] = [
    // Artigos sobre Status dos Pedidos
    {
      id: 'status-pedidos-cliente',
      title: 'Status dos Pedidos - Guia Completo para Clientes',
      category: 'cliente',
      tags: ['status', 'pedidos', 'fluxo', 'processo'],
      priority: 'high',
      lastUpdated: '2024-01-15',
      content: `
# Status dos Pedidos - Guia Completo para Clientes

## Fluxo Completo dos Status

### PENDENTE
O pedido foi criado e está aguardando orçamentos dos fornecedores.

**O que acontece:**
- Fornecedores podem visualizar seu pedido
- Análise dos requisitos e preparação de orçamentos
- Notificações quando orçamentos chegarem

**Tempo típico:** 1-3 dias úteis

**Ações disponíveis:**
- Editar detalhes do pedido
- Cancelar o pedido
- Adicionar informações complementares

---

### EM ANDAMENTO
Você recebeu um ou mais orçamentos e está analisando as propostas.

**O que acontece:**
- Compare preços, prazos e condições
- Analise o histórico dos fornecedores
- Tire dúvidas diretamente com os fornecedores

**Ações disponíveis:**
- Ver detalhes dos orçamentos
- Aprovar um orçamento
- Reprovar orçamentos
- Solicitar ajustes nas propostas

---

### APROVADO
Você escolheu um orçamento e aprovou a proposta.

**O que acontece:**
- O fornecedor é notificado da aprovação
- Detalhes finais são acertados
- Cronograma de execução é definido

**Próximos passos:** O status mudará automaticamente conforme o andamento.

---

### NEGOCIANDO
Você e o fornecedor estão ajustando detalhes da proposta aprovada.

**Possíveis negociações:**
- Forma de pagamento
- Prazo de entrega
- Detalhes técnicos
- Condições especiais

**Ações disponíveis:**
- Trocar mensagens com o fornecedor
- Aceitar condições finais
- Solicitar alterações

---

### AGUARDANDO PEÇA
O fornecedor já encomendou a peça/serviço e está aguardando chegada.

**O que acontece:**
- Peça foi encomendada na fábrica ou distribuidora
- Fornecedor acompanha o prazo de entrega
- Você recebe atualizações de status

**Tempo típico:** Varia conforme disponibilidade da peça (5-30 dias)

---

### EM EXECUÇÃO
O serviço está sendo executado ou a peça está sendo instalada.

**O que acontece:**
- Trabalho em andamento
- Possíveis visitas técnicas
- Atualizações de progresso

**Ações disponíveis:**
- Acompanhar progresso
- Comunicar-se com o fornecedor
- Reportar problemas

---

### FINALIZADO
Serviço concluído com sucesso.

**O que acontece:**
- Trabalho/entrega foi finalizado
- Todas as condições foram cumpridas
- Pedido está encerrado

**Ações disponíveis:**
- Avaliar o fornecedor
- Baixar comprovantes
- Solicitar garantia (se aplicável)

---

### REPROVADO
Todos os orçamentos recebidos foram rejeitados.

**Motivos comuns:**
- Preços acima do orçamento
- Prazos inadequados
- Condições não aceitas

**Ações disponíveis:**
- Reabrir o pedido
- Ajustar requisitos
- Criar novo pedido

---

### CANCELADO
Você decidiu cancelar o pedido.

**Motivos comuns:**
- Mudança de prioridades
- Orçamento indisponível
- Problema resolvido por outros meios

**Nota:** Pedidos cancelados não podem ser reativados.

## Acompanhamento em Tempo Real

- **Dashboard:** Veja todos os status em um lugar
- **Notificações:** Receba atualizações automáticas
- **Filtros:** Organize pedidos por status
- **Detalhes:** Acesse cronologia completa de cada pedido
      `
    },
    {
      id: 'status-orcamentos-cliente',
      title: 'Status dos Orçamentos - Para Clientes',
      category: 'cliente',
      tags: ['orçamentos', 'status', 'aprovação'],
      priority: 'high',
      lastUpdated: '2024-01-15',
      content: `
# Status dos Orçamentos - Para Clientes

## Entendendo os Status dos Orçamentos

### ENVIADO
O fornecedor enviou uma proposta para seu pedido.

**Informações incluídas:**
- Valor total do serviço/peça
- Prazo de entrega
- Condições de pagamento
- Observações técnicas
- Garantias oferecidas

**Ações disponíveis:**
- Analisar proposta detalhadamente
- Comparar com outros orçamentos
- Aprovar ou reprovar
- Solicitar esclarecimentos

---

### APROVADO
Você escolheu e aprovou este orçamento.

**O que acontece:**
- Fornecedor é notificado da escolha
- Outros orçamentos são automaticamente reprovados
- Processo de execução é iniciado
- Cronograma é definido

**Importante:** Apenas um orçamento por pedido pode ser aprovado.

---

### REPROVADO
Você decidiu não aceitar esta proposta.

**Motivos comuns:**
- Preço muito alto
- Prazo inadequado
- Condições não aceitas
- Optou por outro fornecedor

**Nota:** Orçamentos reprovados ficam salvos para histórico.

---

### FINALIZADO
O serviço/entrega foi concluído com sucesso.

**O que acontece:**
- Trabalho foi executado conforme aprovado
- Todas as condições foram cumpridas
- Processo está encerrado

**Ações disponíveis:**
- Avaliar o fornecedor
- Baixar comprovantes e notas
- Acessar garantias
- Solicitar suporte pós-venda

## Como Analisar Orçamentos

### Critérios de Avaliação:

1. **Preço**
   - Compare valores totais
   - Verifique o que está incluso
   - Analise formas de pagamento

2. **Prazo**
   - Urgência do seu pedido
   - Disponibilidade da peça/serviço
   - Cronograma realista

3. **Qualidade do Fornecedor**
   - Avaliações de outros clientes
   - Tempo de mercado
   - Especialização na área

4. **Garantias**
   - Tempo de garantia oferecido
   - Cobertura da garantia
   - Suporte pós-venda

5. **Detalhamento**
   - Clareza da proposta
   - Especificações técnicas
   - Condições contratuais

## Dicas Importantes

- **Compare sempre:** Aguarde receber pelo menos 2-3 orçamentos antes de decidir
- **Leia tudo:** Analise observações e condições especiais
- **Tire dúvidas:** Use o sistema de mensagens para esclarecer pontos
- **Verifique histórico:** Consulte avaliações do fornecedor
- **Considere qualidade:** O mais barato nem sempre é a melhor opção
      `
    },
    {
      id: 'gerador-senhas',
      title: 'Gerador de Senhas Seguras',
      category: 'geral',
      tags: ['senha', 'segurança', 'cadastro', 'gerador'],
      priority: 'medium',
      lastUpdated: '2024-01-15',
      content: `
# Gerador de Senhas Seguras

## Como Usar o Gerador de Senhas

### Localização:
- Disponível na página de cadastro
- Ao lado do campo "Senha"
- Botão "Gerar Sugestões" com ícone de refresh

### Como Funciona:

1. **Clique em "Gerar Sugestões"**
   - Sistema gera 5 senhas diferentes
   - Tamanhos variados: 6 a 10 caracteres
   - Cada senha é única e segura

2. **Escolha uma Senha**
   - Visualize todas as opções
   - Veja o tamanho de cada senha
   - Clique em "Usar" na senha desejada

3. **Senha Aplicada Automaticamente**
   - Campo de senha é preenchido
   - Senha fica oculta por segurança
   - Use o botão para visualizar

## Características das Senhas Geradas

### Composição Segura:
- **Letras maiúsculas** (A-Z)
- **Letras minúsculas** (a-z)
- **Números** (0-9)
- **Símbolos especiais** (!@#$%&*)

### Algoritmo de Segurança:
- **Garantia**: Pelo menos 1 caractere de cada tipo
- **Embaralhamento**: Distribuição aleatória
- **Exclusões**: Evita caracteres confusos (0/O, 1/l)

### Tamanhos Disponíveis:
- **6 caracteres**: Básico
- **7 caracteres**: Bom
- **8 caracteres**: Recomendado
- **9 caracteres**: Muito bom
- **10 caracteres**: Excelente

## Dicas de Segurança

### Boas Práticas:
- **Use senhas únicas** para cada site
- **Troque regularmente** (a cada 6 meses)
- **Anote em local seguro** (gerenciador de senhas)
- **Não compartilhe** com outras pessoas
- **Evite dados pessoais** (nome, data de nascimento)

### Por que Usar o Gerador:
- **Rapidez**: Senhas prontas em segundos
- **Segurança**: Algoritmo profissional
- **Variedade**: Múltiplas opções
- **Facilidade**: Um clique para usar
- **Confiabilidade**: Testado e aprovado
      `
    }
  ];

  const filteredArticles = helpArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const formatContent = (content: string) => {
    return content
      .replace(/\n/g, '<br/>')
      .replace(/### (.*?)(<br\/>|$)/g, '<h3 class="text-xl font-semibold text-gray-900 mt-6 mb-3">$1</h3>')
      .replace(/## (.*?)(<br\/>|$)/g, '<h2 class="text-2xl font-bold mt-8 mb-4" style="color: #ED1C24;">$1</h2>')
      .replace(/# (.*?)(<br\/>|$)/g, '<h1 class="text-3xl font-bold text-gray-900 mt-8 mb-6" style="border-bottom: 3px solid #ED1C24; padding-bottom: 0.5rem;">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/---(<br\/>|$)/g, '<hr class="my-6 border-gray-300"/>')
      .replace(/- (.*?)(<br\/>|$)/g, '<li class="help-list-item">$1</li>');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-12 w-auto object-contain"
              />
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(userType === 'cliente' ? '/cliente/dashboard' : '/fornecedor/dashboard')}
                className="btn btn-outline"
                title="Voltar ao Dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </button>
              <button 
                onClick={() => navigate('/configuracoes')}
                className="btn btn-outline"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configurações
              </button>
              <button 
                onClick={handleLogout} 
                className="btn btn-outline"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold dashboard-title mb-2">
            Central de Ajuda
          </h1>
          <p className="dashboard-subtitle">
            {userType === 'cliente' ? 'Guias e documentação para clientes' : 
             userType === 'fornecedor' ? 'Guias e documentação para fornecedores' : 
             'Documentação completa do sistema PeçaJá & Sobratema'}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full lg:w-[30%] flex-shrink-0">
            <div className="card sticky top-8">
              {/* Search */}
              <div className="mb-6">
                <div className="search-input-container">
                  <Search className="w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Buscar ajuda..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input w-full"
                  />
                </div>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Categorias</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === 'all' ? 'bg-red text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Todos os Artigos
                  </button>

                  <button
                    onClick={() => setSelectedCategory('cliente')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === 'cliente' ? 'bg-red text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Users className="w-4 h-4 inline mr-2" />
                    Clientes
                  </button>
                  
                  <button
                    onClick={() => setSelectedCategory('fornecedor')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === 'fornecedor' ? 'bg-red text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Truck className="w-4 h-4 inline mr-2" />
                    Fornecedores
                  </button>
                  
                  <button
                    onClick={() => setSelectedCategory('geral')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === 'geral' ? 'bg-red text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Settings className="w-4 h-4 inline mr-2" />
                    Geral
                  </button>
                </div>
              </div>

              {/* Articles List */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Artigos</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredArticles.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => setSelectedArticle(article)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedArticle?.id === article.id
                          ? 'border-red bg-red bg-opacity-10'
                          : 'border-gray-200 hover:border-red hover:bg-red hover:bg-opacity-5'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm leading-tight">
                            {article.title}
                          </h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              article.category === 'cliente' ? 'bg-blue-100 text-blue-800' :
                              article.category === 'fornecedor' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {article.category === 'cliente' ? 'Cliente' :
                               article.category === 'fornecedor' ? 'Fornecedor' : 'Geral'}
                            </span>
                            {article.priority === 'high' && (
                              <Star className="w-3 h-3 text-yellow-500" />
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
                
                {filteredArticles.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>Nenhum artigo encontrado</p>
                    <p className="text-sm">Tente ajustar sua busca</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="w-full lg:w-[70%] flex-1">
            <div className="card">
              {selectedArticle ? (
                <div>
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-3 py-1 text-sm rounded-full ${
                        selectedArticle.category === 'cliente' ? 'bg-blue-100 text-blue-800' :
                        selectedArticle.category === 'fornecedor' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedArticle.category === 'cliente' ? 'Cliente' :
                         selectedArticle.category === 'fornecedor' ? 'Fornecedor' : 'Geral'}
                      </span>
                      {selectedArticle.priority === 'high' && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          Alta Prioridade
                        </span>
                      )}
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {selectedArticle.title}
                    </h1>
                    <p className="text-sm text-gray-500">
                      Atualizado em {new Date(selectedArticle.lastUpdated).toLocaleDateString('pt-BR')}
                    </p>
                  </div>

                  <div 
                    className="help-content prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: formatContent(selectedArticle.content)
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-96 text-gray-500">
                  <div className="text-center">
                    <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-medium mb-2">Selecione um artigo</h3>
                    <p>Escolha um tópico da lista ao lado para ver o conteúdo detalhado</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AjudaPage;